import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useSession } from '@/features/auth/useSession';
import { queryKeys } from '@/lib/query-keys';
import type { Notification, ReportReason, SearchResults, TownPulse } from '@/lib/schemas/discovery';

import { discoveryApi } from './api';

/** Polling interval for the bell (spec §8.5). Replaceable with a socket later. */
export const NOTIFICATION_POLL_MS = 60_000;

export function useSearch(query: string, scope: string): UseQueryResult<SearchResults, Error> {
  return useQuery({
    queryKey: queryKeys.search(query, scope),
    queryFn: ({ signal }) => discoveryApi.search(query, scope, signal),
    // Two characters is the API's floor; asking below it only produces a 400.
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useTownPulse(params: Record<string, string>): UseQueryResult<TownPulse, Error> {
  return useQuery({
    queryKey: queryKeys.pulse(params),
    queryFn: ({ signal }) => discoveryApi.pulse(params, signal),
    staleTime: 5 * 60_000,
  });
}

export function useTrendingTags(params: Record<string, string>) {
  return useQuery({
    queryKey: queryKeys.trendingTags(params),
    queryFn: ({ signal }) => discoveryApi.trendingTags({ ...params, limit: '8' }, signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * The bell's unread count.
 *
 * Polled rather than pushed (spec §8.5 asks for 60s), and only while signed in
 * and the tab is visible — a background tab polling forever is a battery and a
 * bandwidth cost for information nobody is looking at.
 */
export function useUnreadCount(): number {
  const { isSignedIn } = useSession();

  const query = useQuery({
    queryKey: queryKeys.unreadCount,
    queryFn: ({ signal }) => discoveryApi.unreadCount(signal),
    enabled: isSignedIn,
    refetchInterval: NOTIFICATION_POLL_MS,
    refetchIntervalInBackground: false,
  });

  return query.data?.unread ?? 0;
}

/**
 * The notification list itself.
 *
 * Unlike the count, this is *not* polled: the bell asks for it when the reader
 * opens the menu, and the count — which is a much cheaper request — is what
 * keeps ticking in the background. `enabled` lets the bell hold the fetch back
 * until the menu is opened for the first time.
 */
export function useNotifications(enabled = true) {
  const { isSignedIn } = useSession();

  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: ({ signal }) => discoveryApi.notifications(signal),
    enabled: isSignedIn && enabled,
    staleTime: 30_000,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notification?: Notification) =>
      discoveryApi.markRead(notification?.notificationIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      void queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount });
    },
  });
}

export function useToggleBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, isBlocked }: { userId: string; isBlocked: boolean }) =>
      isBlocked ? discoveryApi.unblock(userId) : discoveryApi.block(userId),
    onSuccess: () => {
      // Blocking changes what is in the feed, in search, in comments and in
      // notifications — everything that reads content has to be refetched.
      void queryClient.invalidateQueries();
    },
  });
}

export function useBlockedUsers() {
  return useQuery({
    queryKey: queryKeys.blocked,
    queryFn: ({ signal }) => discoveryApi.listBlocked(signal),
  });
}

// --- stretch features (spec §8.7, §8.11) --------------------------------------

export function useMapStats(window: string) {
  return useQuery({
    queryKey: queryKeys.map(window),
    queryFn: ({ signal }) => discoveryApi.map(window, signal),
    // The map is a lot of geometry for a number that moves slowly.
    staleTime: 5 * 60_000,
  });
}

export function useHallOfFame(params: Record<string, string>) {
  return useQuery({
    queryKey: queryKeys.hallOfFame(params),
    queryFn: ({ signal }) => discoveryApi.hallOfFame(params, signal),
    staleTime: 5 * 60_000,
  });
}

// --- safety -----------------------------------------------------------------

export interface ReportTarget {
  kind: 'POST' | 'COMMENT';
  id: string;
  /** What the reader thinks they are reporting: a title, or the first line. */
  label: string;
}

export function useReport() {
  return useMutation({
    mutationFn: ({
      target,
      reason,
      details,
    }: {
      target: ReportTarget;
      reason: ReportReason;
      details?: string;
    }) =>
      target.kind === 'POST'
        ? discoveryApi.reportPost(target.id, reason, details)
        : discoveryApi.reportComment(target.id, reason, details),
  });
}

// --- moderation -------------------------------------------------------------

export function useReports(status: string) {
  return useQuery({
    queryKey: queryKeys.moderationReports(status),
    queryFn: ({ signal }) => discoveryApi.reports(status, signal),
  });
}

export function useOpenReportCount(): number {
  const { user } = useSession();
  const canModerate = user?.role === 'MODERATOR' || user?.role === 'ADMIN';

  const query = useQuery({
    queryKey: queryKeys.moderationCount,
    queryFn: ({ signal }) => discoveryApi.openReportCount(signal),
    enabled: canModerate,
    refetchInterval: NOTIFICATION_POLL_MS,
    refetchIntervalInBackground: false,
  });

  return query.data?.open ?? 0;
}

export function useAuditLog() {
  return useQuery({
    queryKey: queryKeys.moderationAudit,
    queryFn: ({ signal }) => discoveryApi.auditLog(signal),
  });
}

export type ModerationAction =
  | { kind: 'resolve'; id: string; status: 'ACTIONED' | 'DISMISSED' }
  | { kind: 'hidePost'; id: string; reason?: string }
  | { kind: 'removePost'; id: string; reason?: string }
  | { kind: 'removeComment'; id: string; reason?: string }
  | { kind: 'suspendUser'; id: string; reason?: string };

/**
 * One mutation for every moderator action.
 *
 * They all invalidate the same things — the queue, the badge, the audit log and
 * whatever content was acted on — so splitting them into five hooks would be
 * five copies of the same `onSuccess`.
 */
export function useModerationAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (action: ModerationAction) => {
      switch (action.kind) {
        case 'resolve':
          return discoveryApi.resolveReport(action.id, action.status);
        case 'hidePost':
          return discoveryApi.hidePost(action.id, action.reason);
        case 'removePost':
          return discoveryApi.removePost(action.id, action.reason);
        case 'removeComment':
          return discoveryApi.removeComment(action.id, action.reason);
        case 'suspendUser':
          return discoveryApi.suspendUser(action.id, action.reason);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['moderation'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedRoot });
    },
  });
}
